import { useEffect } from 'react';
import { atom, useSetRecoilState } from 'recoil';
import type { PageChatContextHook, PageChatContextResult } from './types';

/**
 * Latest page-declared chat context. Pages publish via `useRegisterChatContext`;
 * the drawer reads at `runtime.send()` time. Default `null` means no page has
 * registered (or the registered hook is still loading); the drawer falls back
 * to a route-only envelope in that case.
 */
export const activePageChatContextState = atom<PageChatContextResult | null>({
  key: 'activePageChatContextState',
  default: null
});

/**
 * Register a page-context hook from inside a page's render tree. The hook runs
 * on every render of the calling component; its return value is published to
 * `activePageChatContextState`. Cleanup clears the atom on unmount.
 *
 * - Last-writer-wins: if two components register at once, the later publish
 *   overwrites the earlier value. Dev-mode emits a `console.warn` when the
 *   overwrite crosses `pageId` boundaries.
 * - Hook throws are caught here so a misbehaving page can't crash the drawer.
 *
 * See docs/architecture/spikes/page-aware-chat-spike.md §3.2 / §3.10.
 * @param hook page-context hook returning the latest result (or `null` while loading).
 */
export const useRegisterChatContext = (hook: PageChatContextHook): void => {
  const setActive = useSetRecoilState(activePageChatContextState);

  let value: PageChatContextResult | null;
  try {
    value = hook();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[useRegisterChatContext] page-context hook threw; treating as null.', err);
    value = null;
  }

  // Publish on every value change. Kept separate from the unmount-clear effect
  // below so a value transition doesn't briefly flash the atom to `null`.
  useEffect(() => {
    setActive((prev) => {
      if (process.env.NODE_ENV !== 'production' && prev && value && prev.pageId !== value.pageId) {
        // eslint-disable-next-line no-console
        console.warn(
          `[useRegisterChatContext] overwriting active page context "${prev.pageId}" with "${value.pageId}". Last writer wins.`
        );
      }
      return value;
    });
  }, [setActive, value]);

  useEffect(
    () => () => {
      setActive(null);
    },
    [setActive]
  );
};
